import {
  forwardRef,
  InputHTMLAttributes,
  ReactElement,
  useState,
  useEffect,
} from "react";
import { cn } from "@repo/ui/lib/utils";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@repo/ui/components/ui/popover";
import { Calendar } from "@repo/ui/components/ui/calendar";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { cva, type VariantProps } from "class-variance-authority";

interface FloatingLabelDateProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof datePickerVariants> {
  id?: string;
  label: string | ReactElement;
  type?: string;
  dateValue?: DateRange;
  ref?: any;
  onBlur?: () => void;
  disabled?: boolean | undefined;
  className?: string;
  // eslint-disable-next-line no-unused-vars
  onDateChange?: (date: DateRange) => void;
}

const datePickerVariants = cva("", {
  variants: {
    variant: {
      default: "h-[70px] pt-3",
      slim: "h-[40px] pt-2",
    },
    variantLabel: {
      default: "",
      slim: "",
    },
    variantLabelActive: {
      default: "-translate-y-3",
      slim: "-translate-y-3",
    },
  },
  defaultVariants: {
    variant: "default",
    variantLabel: "default",
    variantLabelActive: "default",
  },
});

const FloatingLabelDate: React.FC<FloatingLabelDateProps> = forwardRef<
  HTMLInputElement,
  FloatingLabelDateProps
>(
  (
    {
      id,
      label,
      variant,
      dateValue,
      onBlur,
      required,
      disabled,
      className,
      onDateChange,
      ...props
    },
    ref,
  ) => {
    const [date, setDate] = useState<DateRange>();

    useEffect(() => {
      if (dateValue) {
        setDate(dateValue);
      }
    }, []);

    useEffect(() => {
      onDateChange && date && onDateChange(date);
    }, [date]);

    return (
      <Popover>
        <PopoverTrigger asChild>
          <div className="relative flex justify-center items-center">
            <input
              id={id}
              type="text"
              readOnly={true}
              value={
                date?.from && date?.to
                  ? `${format(date.from, "dd-MM-yyyy")} - ${format(date.to, "dd-MM-yyyy")}`
                  : ""
              }
              ref={ref}
              className={cn(
                "block w-full pl-[20px] text-sm font-black text-gray-900 dark:text-primary-white bg-secondary-black/10 dark:bg-primary-white/15 rounded-[10px] focus:outline-none focus:ring-1 focus:ring-secondary-black peer",
                datePickerVariants({ variant }),
                {
                  "ring-0": date || props.defaultValue,
                },
                disabled && "bg-secondary-gray1",
                className,
              )}
              placeholder=" "
              {...props}
            />
            <label
              htmlFor={id}
              className={cn(
                "absolute text-sm text-secondary-black dark:text-primary-white font-normal duration-150 transform scale-100 origin-[0] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:scale-75 peer-focus:-translate-y-3",
                datePickerVariants({ variantLabel: variant }),
                {
                  "left-5 scale-75 font-medium":
                    date || dateValue || props.defaultValue,
                },
                date || dateValue || props.defaultValue
                  ? datePickerVariants({ variantLabelActive: variant })
                  : "",
              )}
            >
              {label} {required ? "*" : ""}
            </label>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            className="p-5"
            required={true}
            mode={"range"}
            captionLayout="dropdown"
            fixedWeeks
            showWeekNumber
            onDayBlur={onBlur}
            selected={date}
            onSelect={(date: DateRange) => {
              setDate(date);
            }}
            disabled={disabled}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

export { FloatingLabelDate };
